<script setup lang="ts">
import { startCase } from "lodash-es";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";
import {
  BadgeSeverity,
  ButtonSeverity,
  ButtonSize,
  isNullish,
  TuBadge,
  TuButton,
  TuTable,
  TuTableHeader,
  TuTableRow,
  TuTooltipWrapper,
} from "@tinyurldev/tu-ui-vue";

import PaymentMethodBrandIcon from "@Modules/billing/components/PaymentMethodBrandIcon.vue";
import { hasPaymentMethod } from "@Modules/billing/Data/Models/SubscriptionPaymentMethod";
import type { BillingSubscriptionRow } from "@Modules/billing/ViewModels/BillingViewModel";
import useTranslation from "@Composables/useTranslation";
import SUBSCRIPTION_TYPE from "@Constants/subscription-type";
import Spinner from "@Components/Spinner.vue";

//
// Setup
const { __ } = useTranslation();
const { smallerOrEqual } = useBreakpoints(breakpointsBootstrapV5);

//
// Props
interface Props {
  subscriptionRows: BillingSubscriptionRow[];
  isReady?: boolean;
  requestedSubscriptionId?: string | null;
  updatedSubscriptionId?: string | null;
  manageSubscription: (subscriptionId: string) => void;
}

withDefaults(defineProps<Props>(), {
  isReady: true,
  requestedSubscriptionId: null,
  updatedSubscriptionId: null,
});

//
// Data
const isMobileView = smallerOrEqual("xl");

//
// Methods
/**
 * The brand the row's card icon is drawn from, empty for a row without a payment method.
 */
function cardBrand(subscription: BillingSubscriptionRow): string {
  return subscription.paymentMethod.brand ?? "";
}
</script>

<template>
  <TuTable>
    <template #header>
      <TuTableHeader>
        <th>{{ __("settings_billing_page.subscription.table.subscription") }}</th>

        <template v-if="!isMobileView">
          <th
            class="subscription-table__card-cell"
            :class="{ 'subscription-table__card-cell--expanded': !isNullish(updatedSubscriptionId) }"
          >
            {{ __("settings_billing_page.subscription.table.card") }}
          </th>
          <th class="subscription-table__card-number-cell">
            &nbsp;
          </th>
          <th class="subscription-table__expiration-cell">
            {{ __("settings_billing_page.subscription.table.expiration_date") }}
          </th>
          <th class="subscription-table__action-cell">
            {{ __("settings_billing_page.subscription.table.action") }}
          </th>
        </template>
      </TuTableHeader>
    </template>

    <TuTableRow v-if="!isReady">
      <td
        class="subscription-table__spinner-cell _spinner"
        :colspan="isMobileView ? 1 : 5"
      >
        <Spinner size="base" />
      </td>
    </TuTableRow>

    <template v-else-if="!isMobileView">
      <TuTableRow
        v-for="subscription in subscriptionRows"
        :key="`${subscription.id}-desktop`"
        class="_subscription_row"
      >
        <td>
          <span
            v-if="subscription.type === SUBSCRIPTION_TYPE.DEFAULT"
            class="subscription-table__plan-highlighter _subscription_plan_badge"
          >
            {{ __("settings_billing_page.subscription.table.plan") }}
          </span>

          <span class="_subscription_name">{{ __(`billing.${subscription.name}`) }}</span>

          <span class="subscription-table__interval _subscription_interval">
            {{ startCase(__(`billing.interval.${subscription.interval}`)) }}
          </span>
          <TuTooltipWrapper
            v-if="subscription.hasTaxTooltip"
            class="subscription-table__tax-compliance-tooltip _subscription_tax_tooltip"
            :tooltip="__('settings_billing_page.subscription.table.paddle_tax_compliance')"
          >
            <i class="fa-solid fa-info-circle subscription-table__tooltip-icon" />
          </TuTooltipWrapper>
        </td>
        <td
          class="subscription-table__card-cell"
          :class="{ 'subscription-table__card-cell--expanded': !isNullish(updatedSubscriptionId) }"
        >
          <TuBadge
            v-if="subscription.id === updatedSubscriptionId"
            class="subscription-table__card-updated-badge _subscription_card_updated"
            :severity="BadgeSeverity.SECONDARY"
            icon-class="fa fa-check"
            :label="__('settings_billing_page.subscription.table.card_updated')"
          />
          <template v-else>
            <PaymentMethodBrandIcon
              v-if="hasPaymentMethod(subscription.paymentMethod)"
              class="_subscription_card_icon"
              :brand="cardBrand(subscription)"
            />
            <span
              v-else
              class="_subscription_card_missing"
            >—</span>
          </template>
        </td>
        <td class="subscription-table__card-number-cell _subscription_last_four">
          {{ subscription.paymentMethod.lastFourNumbers }}
        </td>
        <td class="subscription-table__expiration-cell _subscription_expiration">
          {{ subscription.paymentMethod.expirationDate }}
        </td>
        <td
          align="right"
          class="subscription-table__action-cell"
        >
          <TuButton
            v-if="subscription.hasActionButton"
            class="subscription-table__update-button _subscription_update"
            :class="{ _subscription_update_loading: subscription.id === requestedSubscriptionId }"
            :label="__('settings_billing_page.subscription.table.update')"
            :severity="ButtonSeverity.SECONDARY"
            :size="ButtonSize.MEDIUM"
            is-outlined
            :is-disabled="!isNullish(requestedSubscriptionId)"
            :is-loading="subscription.id === requestedSubscriptionId"
            @click="manageSubscription(subscription.id)"
          />
        </td>
      </TuTableRow>
    </template>

    <template v-else>
      <TuTableRow
        v-for="subscription in subscriptionRows"
        :key="`${subscription.id}-mobile`"
        class="_subscription_row"
      >
        <td>
          <div class="subscription-table__mobile-row subscription-table__mobile-row--plan">
            <span
              v-if="subscription.type === SUBSCRIPTION_TYPE.DEFAULT"
              class="subscription-table__mobile-plan-row__highlighter _subscription_plan_badge"
            >
              {{ __("settings_billing_page.subscription.table.plan") }}
            </span>

            <span class="_subscription_name">{{ __(`billing.${subscription.name}`) }}</span>

            <span class="subscription-table__interval _subscription_interval">
              {{ startCase(__(`billing.interval.${subscription.interval}`)) }}
            </span>
            <TuTooltipWrapper
              v-if="subscription.hasTaxTooltip"
              class="subscription-table__tax-compliance-tooltip _subscription_tax_tooltip"
              :tooltip="__('settings_billing_page.subscription.table.paddle_tax_compliance')"
            >
              <i class="fa-solid fa-info-circle subscription-table__tooltip-icon" />
            </TuTooltipWrapper>
          </div>
          <div class="subscription-table__mobile-row subscription-table__mobile-row--card">
            <p class="subscription-table__mobile-title">
              {{ __("settings_billing_page.subscription.table.card") }}:
            </p>
            <div class="subscription-table__mobile-content">
              <TuBadge
                v-if="subscription.id === updatedSubscriptionId"
                class="subscription-table__card-updated-badge _subscription_card_updated"
                :severity="BadgeSeverity.SECONDARY"
                icon-class="fa fa-check"
                :label="__('settings_billing_page.subscription.table.card_updated')"
              />
              <div
                v-else
                class="subscription-table__mobile-card-brand"
              >
                <PaymentMethodBrandIcon
                  v-if="hasPaymentMethod(subscription.paymentMethod)"
                  class="_subscription_card_icon"
                  :brand="cardBrand(subscription)"
                />
                <span
                  v-else
                  class="_subscription_card_missing"
                >—</span>
                <span class="_subscription_last_four">{{ subscription.paymentMethod.lastFourNumbers }}</span>
              </div>
            </div>
          </div>
          <div class="subscription-table__mobile-row">
            <p class="subscription-table__mobile-title">
              {{ __("settings_billing_page.subscription.table.expiration_date") }}:
            </p>
            <div class="subscription-table__mobile-content _subscription_expiration">
              {{ subscription.paymentMethod.expirationDate }}
            </div>
          </div>
          <div class="subscription-table__mobile-row">
            <p class="subscription-table__mobile-title">
              {{ __("settings_billing_page.subscription.table.action") }}:
            </p>
            <div class="subscription-table__mobile-content">
              <TuButton
                v-if="subscription.hasActionButton"
                class="subscription-table__update-button _subscription_update"
                :class="{ _subscription_update_loading: subscription.id === requestedSubscriptionId }"
                :label="__('settings_billing_page.subscription.table.update')"
                :severity="ButtonSeverity.SECONDARY"
                :size="ButtonSize.MEDIUM"
                is-outlined
                :is-disabled="!isNullish(requestedSubscriptionId)"
                :is-loading="subscription.id === requestedSubscriptionId"
                @click="manageSubscription(subscription.id)"
              />
            </div>
          </div>
        </td>
      </TuTableRow>
    </template>
  </TuTable>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;

.subscription-table {
  $self: &;

  &__card-cell {
    width: 65px;
    text-align: center;
    white-space: nowrap;

    &--expanded {
      width: 150px;
      text-align: left;
    }
  }

  &__card-number-cell {
    width: 60px;
  }

  &__expiration-cell {
    width: 150px;
  }

  &__action-cell {
    width: 90px;
    white-space: nowrap;
  }

  &__plan-highlighter {
    font-weight: typography.$font-weight-bold;
  }

  &__update-button {
    border-radius: border.$small-radius;
  }

  &__spinner-cell {
    padding: spacing.$xs;
  }

  &__mobile-row {
    &:not(:first-child) {
      display: flex;
    }

    &:not(:last-child) {
      margin-bottom: spacing.$xxxs;
    }

    &--plan {
      #{$self}__mobile-plan-row__highlighter {
        font-weight: typography.$font-weight-bold;
      }
    }
  }

  &__mobile-title,
  &__mobile-content {
    flex: 1;
  }

  &__mobile-title {
    font-weight: typography.$font-weight-bold;
    margin-bottom: 0;
  }

  &__mobile-card-brand {
    display: flex;
    align-items: center;
    gap: spacing.$xxxs;
  }

  &__tooltip-icon {
    color: color.$disabled;
  }
}
</style>
